import React, { useMemo } from "react";
import { ChevronRight, Home } from "lucide-react";
import { 
  getBreadcrumbNodes, 
  generateBreadcrumbJsonLd, 
  BreadcrumbNode,
  createAbsoluteUrl
} from "../../utils/breadcrumbRegistry";

export interface BreadcrumbItem {
  label: string;
  path: string;
}

interface SeoBreadcrumbsProps {
  items?: BreadcrumbItem[];
  path?: string;
  onNavigate: (path: string) => void;
  siteUrl?: string;
}

const DEFAULT_SITE_URL = "https://getbiznearyou.com";

export function SeoBreadcrumbs({
  items,
  path,
  onNavigate,
  siteUrl = DEFAULT_SITE_URL,
}: SeoBreadcrumbsProps) {
  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    targetPath: string,
  ) => {
    event.preventDefault();
    onNavigate(targetPath);
  };

  // Resolve current active path
  const currentPath = useMemo(() => {
    if (path) return path;
    if (items && items.length > 0) {
      return items[items.length - 1].path;
    }
    if (typeof window !== "undefined") {
      return window.location.pathname;
    }
    return "/";
  }, [path, items]);

  // Derived breadcrumb hierarchy nodes (guaranteed to start with Home)
  const breadcrumbNodes = useMemo(() => {
    if (items && items.length > 0) {
      const hasHome = items[0].path === "/" || items[0].label.toLowerCase() === "home";
      if (hasHome) return items;
      return [{ label: "Home", path: "/" }, ...items];
    }
    return getBreadcrumbNodes(currentPath);
  }, [items, currentPath]);

  // Derived JSON-LD Schema
  const breadcrumbSchema = useMemo(() => {
    if (breadcrumbNodes.length < 2) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbNodes.map((node, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: node.label,
        item: createAbsoluteUrl(node.path, siteUrl),
      })),
    };
  }, [breadcrumbNodes, siteUrl]);

  if (!breadcrumbNodes || breadcrumbNodes.length < 2) {
    return null;
  }

  // Render items omitting the first 'Home' node for the loop, as Home is rendered with icon
  const homeNode = breadcrumbNodes[0];
  const trailingNodes = breadcrumbNodes.slice(1);

  const breadcrumbJson = breadcrumbSchema
    ? JSON.stringify(breadcrumbSchema).replace(/</g, "\\u003c")
    : null;

  return (
    <>
      {breadcrumbJson && (
        <script
          id="breadcrumb-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: breadcrumbJson,
          }}
        />
      )}

      <nav
        aria-label="Breadcrumb"
        className="mb-6 flex flex-wrap items-center gap-1.5 py-2 text-xs text-slate-500"
      >
        <a
          href={homeNode.path}
          onClick={(event) => handleLinkClick(event, homeNode.path)}
          className="flex cursor-pointer items-center gap-1 font-medium transition hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          <Home className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{homeNode.label}</span>
        </a>

        {trailingNodes.map((item, index) => {
          const isLast = index === trailingNodes.length - 1;

          return (
            <React.Fragment key={`${item.path}-${index}`}>
              <ChevronRight
                className="h-3 w-3 flex-shrink-0 text-slate-300 dark:text-slate-600"
                aria-hidden="true"
              />

              {isLast ? (
                <span
                  aria-current="page"
                  className="max-w-[200px] truncate font-semibold text-slate-800 dark:text-slate-200 sm:max-w-none"
                >
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.path}
                  onClick={(event) => handleLinkClick(event, item.path)}
                  className="max-w-[150px] cursor-pointer truncate font-medium transition hover:text-indigo-600 dark:hover:text-indigo-400 sm:max-w-none"
                >
                  {item.label}
                </a>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
}
